/** Dynamic support form field definitions (stored as JSON on DynamicForm). */

export const SUPPORT_FORM_FIELD_TYPES = [
  "text",
  "textarea",
  "email",
  "order",
  "number",
  "select",
  "date",
  "amount",
  "file",
] as const;

export type SupportFormFieldType = (typeof SUPPORT_FORM_FIELD_TYPES)[number];

export interface SupportFormField {
  key: string;
  label: string;
  type: SupportFormFieldType;
  required: boolean;
  options?: string[];
}

export const SUPPORT_FORM_FIELD_TYPE_LABELS: Record<SupportFormFieldType, string> = {
  text: "Text",
  textarea: "Textarea",
  email: "Email",
  order: "Order number",
  number: "Number",
  select: "Dropdown",
  date: "Date",
  amount: "Amount (RM)",
  file: "Attachment",
};

export function isSupportFormFieldType(value: string): value is SupportFormFieldType {
  return (SUPPORT_FORM_FIELD_TYPES as readonly string[]).includes(value);
}

export const SUPPORT_CASE_MAX_COMMENT_FILES = 5;

export const SUPPORT_CASE_POLL_MS = 8000;

export type SupportCaseThreadSide = "customer" | "staff";

export interface SupportCaseOpeningLine {
  key: string;
  label: string;
  type: SupportFormFieldType;
  displayValue: string;
  fileKey?: string;
}

export interface SupportCaseThreadItem {
  id: string;
  kind: "opening" | "comment";
  side: SupportCaseThreadSide;
  body: string;
  createdAt: string;
  fileKeys: string[];
  openingLines?: SupportCaseOpeningLine[];
}

export interface SupportCaseCommentInput {
  id: string;
  authorId: string | null;
  body: string;
  fileKeys: string[];
  createdAt: string;
}

export interface SupportCaseThreadInput {
  id: string;
  userId: string | null;
  createdAt: string;
  data: Record<string, unknown> | null;
  comments: SupportCaseCommentInput[];
}

function formatOpeningDisplayValue(type: SupportFormFieldType, raw: unknown): { text: string; fileKey?: string } {
  if (raw === undefined || raw === null || raw === "") {
    return { text: "—" };
  }
  if (type === "file" && typeof raw === "string") {
    return { text: "Attachment", fileKey: raw };
  }
  if (type === "amount" || type === "number") {
    const n = typeof raw === "number" ? raw : Number(String(raw));
    return { text: Number.isFinite(n) ? String(n) : String(raw) };
  }
  if (typeof raw === "object") {
    return { text: JSON.stringify(raw) };
  }
  return { text: String(raw) };
}

export function buildSupportCaseThread(
  caseRow: SupportCaseThreadInput,
  formFields: SupportFormField[],
): SupportCaseThreadItem[] {
  const items: SupportCaseThreadItem[] = [];
  const data = caseRow.data ?? {};
  const fieldMap = new Map(formFields.map((f) => [f.key, f]));

  const openingLines: SupportCaseOpeningLine[] = [];
  const seenKeys = new Set<string>();

  for (const field of formFields) {
    seenKeys.add(field.key);
    const raw = data[field.key];
    const formatted = formatOpeningDisplayValue(field.type, raw);
    openingLines.push({
      key: field.key,
      label: field.label,
      type: field.type,
      displayValue: formatted.text,
      fileKey: formatted.fileKey,
    });
  }

  for (const [key, raw] of Object.entries(data)) {
    if (seenKeys.has(key)) continue;
    const formatted = formatOpeningDisplayValue("text", raw);
    openingLines.push({
      key,
      label: key,
      type: "text",
      displayValue: formatted.text,
      fileKey: formatted.fileKey,
    });
  }

  const openingBody =
    openingLines.length > 0
      ? openingLines.map((l) => `${l.label}: ${l.displayValue}`).join("\n")
      : "Request submitted";

  items.push({
    id: `opening-${caseRow.id}`,
    kind: "opening",
    side: "customer",
    body: openingBody,
    createdAt: caseRow.createdAt,
    fileKeys: [],
    openingLines,
  });

  const caseUserId = caseRow.userId;
  for (const comment of caseRow.comments) {
    const side: SupportCaseThreadSide =
      caseUserId && comment.authorId === caseUserId ? "customer" : "staff";
    items.push({
      id: comment.id,
      kind: "comment",
      side,
      body: comment.body,
      createdAt: comment.createdAt,
      fileKeys: comment.fileKeys ?? [],
    });
  }

  return items.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

export function isSupportCaseTerminal(status: string): boolean {
  return status === "RESOLVED" || status === "REJECTED";
}
